import React, { createContext, useContext, useEffect, useState } from 'react'

import { setFocus } from '@noriginmedia/norigin-spatial-navigation'
import {
  CURSOR,
  NavigationMode,
  NavigationModeContextValue,
  NavigationModeProviderProps,
  SPATIAL,
} from './types'
import { useSidePanel } from '../../../ga/src/components/SidePanel/SidePanelProvider'

const NavigationModeContext = createContext<
  NavigationModeContextValue | undefined
>(undefined)

export function NavigationModeProvider({
  children,
}: NavigationModeProviderProps) {
  const [navigationMode, setNavigationMode] =
    useState<NavigationMode>('spatial')
  const [lastFocusKey, setLastFocusKey] = useState<string>()
  const { isSidePanelOpened } = useSidePanel()

  const switchMode = React.useCallback(
    (isCursorMode: boolean) => {
      if (isCursorMode) {
        if (navigationMode !== CURSOR) {
          setLastFocusKey(undefined)
          setFocus(null)
          setNavigationMode(CURSOR)
        }
      } else if (navigationMode !== SPATIAL) {
        if (isSidePanelOpened) {
          setFocus('SIDE_PANEL')
        } else if (lastFocusKey) {
          setFocus(lastFocusKey)
        }
        setNavigationMode(SPATIAL)
      }
    },
    [navigationMode, lastFocusKey, isSidePanelOpened]
  )

  const handleCursorStateChange = React.useCallback(
    (ev) => {
      switchMode(ev.detail?.visibility)
    },
    [switchMode]
  )

  const handleWheel = React.useCallback(() => {
    switchMode(true)
  }, [switchMode])

  const handleKeyDown = React.useCallback(
    (event: KeyboardEvent) => {
      // TODO Create an centralized list of keys that indicate navigation intent for global use
      const arrowKeys = [
        'ArrowUp',
        'ArrowDown',
        'ArrowLeft',
        'ArrowRight',
        'Enter',
      ]

      const isArrowKey = arrowKeys.includes(event.key)

      if (isArrowKey) {
        switchMode(false)
      }
    },
    [switchMode]
  )

  useEffect(() => {
    document.addEventListener('cursorStateChange', handleCursorStateChange)
    window.addEventListener('wheel', handleWheel)
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('cursorStateChange', handleCursorStateChange)
      window.removeEventListener('wheel', handleWheel)
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [handleCursorStateChange, handleWheel, handleKeyDown])

  const contextValue = React.useMemo(
    () => ({
      isCursorMode: navigationMode === CURSOR,
      lastFocusKey,
      setLastFocusKey,
    }),
    [navigationMode, lastFocusKey]
  )

  return (
    <NavigationModeContext.Provider value={contextValue}>
      {children}
    </NavigationModeContext.Provider>
  )
}

export const useNavigationMode = (): NavigationModeContextValue => {
  const context = useContext(NavigationModeContext)
  if (!context) {
    throw new Error(
      'useNavigationMode must be used within a NavigationModeProvider'
    )
  }
  return context
}

export default NavigationModeProvider
